import type { LoadTemplateForm } from './types';

export const EMPTY_TEMPLATE: LoadTemplateForm = {
  name: '',
  capacityLabel: '',
  enableCarrierRate: true,
  isDefault: false,
};

/** The only hard requirement — everything else has a usable default. */
export const validateTemplate = (form: LoadTemplateForm): string | null =>
  form.name.trim() ? null : 'Name is required.';

/** A saved template back into the edit form. */
export const readTemplateForm = (row: any): LoadTemplateForm => ({
  name: row?.name ?? '',
  capacityLabel: row?.capacityLabel ?? '',
  // Both default on, matching the create form and the server default.
  enableCarrierRate: row?.enableCarrierRate ?? true,
  isDefault: row?.isDefault ?? false,
});

/**
 * Which delivery locations a template is currently assigned to.
 *
 * Prefers the flat id list when the endpoint provides one; otherwise reads
 * `deliveryAddressId` off each join row — the join row's own `id` is not the
 * location, and using it would unassign every location on the next save.
 */
export const readAssignedLocationIds = (row: any): string[] => {
  if (Array.isArray(row?.assignedLocationIds)) {
    return row.assignedLocationIds;
  }

  return (row?.assignedLocations ?? [])
    .map((location: any) => location.deliveryAddressId ?? location.id)
    .filter(Boolean);
};

/**
 * The create/update body.
 *
 * `capacityLabel` sends "" rather than being omitted: it is a label the
 * customer sees on the order form, and clearing it is a real edit.
 *
 * `isDefault` is the org-wide default — `POST /organization/load-options`
 * clears the flag on every sibling in the same transaction, so setting it here
 * unsets whichever template held it. Note this is `isDefault`, not the older
 * unrelated `default` column.
 */
export const buildTemplatePayload = (form: LoadTemplateForm, locationIds: string[]) => ({
  name: form.name.trim(),
  capacityLabel: form.capacityLabel?.trim() || '',
  enableCarrierRate: !!form.enableCarrierRate,
  isDefault: !!form.isDefault,
  locationIds,
});

/**
 * Save errors worth explaining.
 *
 * LOCATION_REQUIRES_LOAD_OPTION is the interesting one: unassigning a template
 * would leave a delivery location with no fulfilment method at all, and a load
 * to that location could then never be approved. The server sends its own
 * message, which is preferred when present.
 */
const SAVE_ERROR_MESSAGES: Record<string, string> = {
  LOCATION_REQUIRES_LOAD_OPTION:
    'This template cannot be removed because it is the only fulfilment method for one of the selected locations.',
  INVALID_CUSTOMER_LOCATION:
    'One or more selected customer locations are invalid or no longer available.',
  INVALID_LOCATION_IDS: 'Invalid customer location selection.',
};

export const templateSaveErrorMessage = (
  code: unknown,
  serverMessage?: string,
  fallback = 'Update failed.'
): string => {
  const known = SAVE_ERROR_MESSAGES[String(code)];

  if (known) {
    return serverMessage || known;
  }

  return code ? String(code) : fallback;
};
